import React from "react";
import { employeesService } from "services";

// Paleta de colores de fondo para el fallback de iniciales (tintes suaves).
const INITIALS_COLORS = [
	{ bg: "#E3F2FD", fg: "#1565C0" },
	{ bg: "#F3E5F5", fg: "#6A1B9A" },
	{ bg: "#E8F5E9", fg: "#2E7D32" },
	{ bg: "#FFF3E0", fg: "#E65100" },
	{ bg: "#FCE4EC", fg: "#AD1457" },
	{ bg: "#E0F7FA", fg: "#00838F" },
	{ bg: "#F1F8E9", fg: "#558B2F" },
	{ bg: "#EDE7F6", fg: "#4527A0" },
	{ bg: "#FFF8E1", fg: "#BF360C" },
];

const getColorForKey = (key) => {
	const str = String(key || "");
	let hash = 0;
	for (let i = 0; i < str.length; i += 1) {
		hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
	}
	return INITIALS_COLORS[hash % INITIALS_COLORS.length];
};

const computeInitials = (rawName) => {
	if (!rawName || typeof rawName !== "string") return "";
	const trimmed = rawName.trim();
	if (!trimmed || trimmed === "Avatar") return "";
	const parts = trimmed.split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "";
	if (parts.length === 1) {
		return parts[0].slice(0, 2).toUpperCase();
	}
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

class Avatar extends React.Component {
	_isMounted = false;

	constructor(props) {
		super(props);
		this.state = {
			pic: "",
			imgError: false,
		};
	}

	componentDidMount() {
		this._isMounted = true;
		this.getImage();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.userId !== this.props.userId) {
			if (this._isMounted) {
				this.setState({ pic: "", imgError: false });
			}
			this.getImage();
		}
	}

	componentWillUnmount() {
		this._isMounted = false;
	}

	openDatabase() {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open("avatarsDB", 1);

			request.onupgradeneeded = (event) => {
				const db = event.target.result;
				db.createObjectStore("avatars", { keyPath: "userId" });
			};

			request.onsuccess = (event) => {
				resolve(event.target.result);
			};

			request.onerror = (event) => {
				reject(event.target.errorCode);
			};
		});
	}

	getAvatarFromDB(userId) {
		return new Promise(async (resolve, reject) => {
			if (!userId) {
				resolve(null);
				return;
			}

			try {
				const db = await this.openDatabase();
				const transaction = db.transaction(["avatars"], "readonly");
				const store = transaction.objectStore("avatars");
				const request = store.get(userId);

				request.onsuccess = () => {
					resolve(request.result ? request.result.img : null);
				};

				request.onerror = (event) => {
					reject(
						`Error al intentar recuperar el avatar: ${event.target.errorCode}`
					);
				};
			} catch (error) {
				reject(
					`Error inesperado al intentar recuperar el avatar: ${error.message}`
				);
			}
		});
	}

	saveAvatarToDB(userId, img) {
		return new Promise(async (resolve, reject) => {
			const db = await this.openDatabase();
			const transaction = db.transaction(["avatars"], "readwrite");
			const store = transaction.objectStore("avatars");
			const request = store.put({ userId, img });

			request.onsuccess = () => {
				resolve();
			};

			request.onerror = (event) => {
				reject(event.target.errorCode);
			};
		});
	}

	async getImage() {
		try {
			const storageData = await this.getAvatarFromDB(this.props.userId);
			let img = "";

			if (storageData !== null && storageData !== undefined) {
				img = storageData;
			} else if (this.props.userId) {
				img = await employeesService.getAvatar(this.props.userId);
				await this.saveAvatarToDB(this.props.userId, img);
			}

			if (this._isMounted) {
				this.setState({
					pic: img || "",
					imgError: false,
				});
			}
		} catch (err) {
			if (this._isMounted) {
				this.setState({ pic: "", imgError: true });
			}
		}
	}

	handleImgError = () => {
		if (this._isMounted) {
			this.setState({ imgError: true });
		}
	};

	render() {
		const {
			onClick,
			className = "",
			style,
			alt,
			title,
			name,
		} = this.props;
		const { pic, imgError } = this.state;

		const displayName = name || alt;
		const altText = alt || name || "Avatar";
		const titleText = title !== undefined ? title : altText;

		// Detectamos src "vacía" de forma robusta: sin pic, pic con solo
		// espacios, o pic que claramente no es una imagen válida (por ejemplo,
		// cuerpo de respuesta 404).
		const picIsEmpty =
			!pic ||
			typeof pic !== "string" ||
			pic.trim() === "" ||
			pic.trim() === "null";

		const shouldShowInitials = imgError || picIsEmpty;
		const initials = computeInitials(displayName);

		if (shouldShowInitials && initials) {
			const colors = getColorForKey(this.props.userId || displayName);
			return (
				<span
					onClick={onClick}
					className={`avatar avatar-initials ${className}`}
					title={titleText}
					aria-label={altText}
					role="img"
					style={{
						backgroundColor: colors.bg,
						color: colors.fg,
						...style,
					}}
				>
					{initials}
				</span>
			);
		}

		if (shouldShowInitials) {
			// Sin nombre válido: render de una "píldora" neutra en vez de
			// la imagen rota.
			return (
				<span
					onClick={onClick}
					className={`avatar avatar-initials avatar-initials--empty ${className}`}
					title={titleText}
					aria-label={altText}
					role="img"
					style={style}
				/>
			);
		}

		return (
			<img
				onClick={onClick}
				onError={this.handleImgError}
				src={pic}
				className={`avatar ${className}`}
				alt={altText}
				title={titleText}
				style={style}
			/>
		);
	}
}

export default Avatar;
